import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../store/supplierCrmOrg.store';
import organizationsService from '../../services/crm/organizationsService';

// Tab components
import ProjectsTab from './tabs/ProjectsTab';
import TasksTab from './tabs/TasksTab';
import MyTasksTab from './tabs/MyTasksTab';
import TeamWorkloadView from './components/TeamWorkloadView';
import GanttChartView from './components/GanttChartView';
import ReportsDashboard from './components/ReportsDashboard';
import MilestonesPanel from './components/MilestonesPanel';
import ActivityFeedPanel from './components/ActivityFeedPanel';
import AutomationsTab from './tabs/AutomationsTab';
import PortfoliosTab from './tabs/PortfoliosTab';
import WorkloadTab from './tabs/WorkloadTab';
import GoalsTab from './tabs/GoalsTab';
import { tasksService } from '../../services/projectManagement/tasksService';
import milestonesService, { type Milestone } from '../../services/projectManagement/milestonesService';

type TabId = 'projects' | 'my-tasks' | 'tasks' | 'portfolios' | 'workload' | 'goals' | 'team' | 'timeline' | 'automations' | 'reports';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.ComponentType;
  requiresTeamPlan?: boolean;
}

const TABS: Tab[] = [
  { id: 'projects', label: 'Projects', icon: '📁', component: ProjectsTab },
  { id: 'my-tasks', label: 'My Tasks', icon: '👤', component: MyTasksTab },
  { id: 'tasks', label: 'Tasks', icon: '✓', component: TasksTab },
  { id: 'portfolios', label: 'Portfolios', icon: '🧺', component: PortfoliosTab },
  { id: 'workload', label: 'Workload', icon: '🧮', component: WorkloadTab, requiresTeamPlan: true },
  { id: 'goals', label: 'Goals', icon: '🎯', component: GoalsTab },
  { id: 'team', label: 'Team', icon: '👥', component: () => <div>Team</div>, requiresTeamPlan: true },
  { id: 'timeline', label: 'Timeline', icon: '📅', component: () => <div>Timeline</div> },
  { id: 'automations', label: 'Automations', icon: '⚡', component: AutomationsTab, requiresTeamPlan: true },
  { id: 'reports', label: 'Reports', icon: '📊', component: () => <div>Reports</div> },
];

// Timeline Tab wrapper with data loading
const TimelineView: React.FC<{ organizationId: string; onTaskClick: (taskId: string) => void }> = ({ organizationId, onTaskClick }) => {
  const [tasks, setTasks] = React.useState<any[]>([]);
  const [milestones, setMilestones] = React.useState<Milestone[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      const [tasksRes, milestonesRes] = await Promise.all([
        tasksService.list({ organization_id: organizationId }),
        milestonesService.list({ organization_id: organizationId }),
      ]);
      setTasks(tasksRes.data || []);
      setMilestones(milestonesRes.data || []);
      setLoading(false);
    };
    void fetchTasks();
  }, [organizationId]);

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading timeline...</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <GanttChartView
          tasks={tasks}
          milestones={milestones}
          onTaskClick={onTaskClick}
          onMilestoneClick={(milestone) => {
            toast.success(`Milestone: ${milestone.name}`);
          }}
        />
      </div>
      <div className="space-y-6">
        <MilestonesPanel organizationId={organizationId} />
        <ActivityFeedPanel organizationId={organizationId} />
      </div>
    </div>
  );
};

const ProjectManagementHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped } = useSupplierCrmOrgStore();
  
  const [activeTab, setActiveTab] = useState<TabId>('projects');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // For now, assume team plan - subscription checking will be added when user module is integrated
  const isTeamPlan = true; // TODO: Replace with actual subscription check

  // Bootstrap organizations on mount
  useEffect(() => {
    const bootstrapOrganizations = async () => {
      if (!user?.id) return;
      
      try {
        const response = await organizationsService.list();
        const list = response.data || [];

        if (list.length === 0) {
          const created = await organizationsService.create('My Organization');
          const createdOrg = created.data;
          if (createdOrg) {
            setActiveOrgId(createdOrg.id);
          }
        } else {
          if (!activeOrgId) {
            setActiveOrgId(list[0]?.id ?? null);
          }
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organizations:', err);
        toast.error(err?.message || 'Failed to load organizations');
      } finally {
        setBootstrapped(true);
      }
    };

    void bootstrapOrganizations();
  }, [user?.id, activeOrgId, setActiveOrgId, setBootstrapped]);

  // Set document title
  useEffect(() => {
    document.title = 'Project Management | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Parse active tab from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.find(t => t.id === hash)) {
      const tab = TABS.find(t => t.id === hash);
      // Check if tab requires team plan
      if (tab?.requiresTeamPlan && !isTeamPlan) {
        setShowUpgradeModal(true);
        return;
      }
      setActiveTab(hash as TabId);
    }
  }, [location.hash, isTeamPlan]);

  const handleTabChange = (tabId: TabId) => {
    const tab = TABS.find(t => t.id === tabId);
    
    // Check if tab requires team plan
    if (tab?.requiresTeamPlan && !isTeamPlan) {
      setShowUpgradeModal(true);
      return;
    }

    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isBootstrapped) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Initializing Project Management...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrgId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Project Management</h1>
            <Link
              to="/help?article=project-management-tabs"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Help
            </Link>
          </div>
          <p className="mt-2 text-gray-600">Manage projects, tasks, and team collaboration</p>
        </div>
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-100">
          <div className="mx-auto max-w-md">
            <div className="mb-4 text-4xl">🏢</div>
            <h3 className="text-lg font-semibold text-gray-900">No Organization Selected</h3>
            <p className="mt-2 text-sm text-gray-600">
              Please create or select an organization to access Project Management.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Project Management</h1>
            <Link
              to="/help?article=project-management-tabs"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Help
            </Link>
          </div>
          <p className="mt-2 text-gray-600">Manage projects, tasks, and team collaboration</p>
        </div>

        {/* Solo Plan Upgrade Banner (if applicable) */}
        {!isTeamPlan && (
          <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">👥</span>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900">Unlock Team Collaboration</h4>
                <p className="mt-1 text-sm text-blue-700">
                  Upgrade to Team plan (£49/mo) to assign tasks, track workload, and access team reports.
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button 
                    onClick={() => navigate('/upgrade')}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Upgrade to Team Plan
                  </button>
                  <a href="/pricing" className="text-sm text-blue-600 hover:underline">
                    Compare plans
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6" aria-label="Tabs">
              {TABS.map((tab) => {
                const isLocked = tab.requiresTeamPlan && !isTeamPlan;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                      ${activeTab === tab.id
                        ? 'border-primary text-primary'
                        : isLocked
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }
                    `}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                    disabled={isLocked}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    <span>{tab.label}</span>
                    {isLocked && <span className="text-xs">🔒</span>}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'projects' && <ProjectsTab />}
            {activeTab === 'my-tasks' && <MyTasksTab />}
            {activeTab === 'tasks' && <TasksTab />}
            {activeTab === 'portfolios' && <PortfoliosTab />}
            {activeTab === 'workload' && <WorkloadTab />}
            {activeTab === 'goals' && <GoalsTab />}
            {activeTab === 'team' && activeOrgId && (
              <TeamWorkloadView 
                organizationId={activeOrgId} 
                onTaskClick={(taskId) => setSelectedTaskId(taskId)} 
              />
            )}
            {activeTab === 'timeline' && <TimelineView organizationId={activeOrgId} onTaskClick={(taskId) => setSelectedTaskId(taskId)} />}
            {activeTab === 'automations' && <AutomationsTab />}
            {activeTab === 'reports' && activeOrgId && <ReportsDashboard organizationId={activeOrgId} />}
          </div>
        </div>

        {/* Quick Actions Hint */}
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            💡 Tip: Use keyboard shortcuts for faster navigation (coming soon)
          </p>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Team Plan Required</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-6">
              <div className="mb-4 text-4xl text-center">👥</div>
              <p className="text-sm text-gray-600">
                This feature is available on the Team plan (£49/month) which includes:
              </p>
              <ul className="mt-3 space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Up to 3 team members</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Task assignment and collaboration</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Team workload tracking</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Advanced team reports</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Unlimited automations</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/upgrade');
                }}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagementHub;
